import { Link } from "react-router";
import { Archive, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ARCHIVE_DATE } from "@/config/archive";
import { useSeoMetadata } from "@/hooks/useSeoMetadata";

interface ArchivedFeaturePageProps {
  /** Human name of the retired surface, e.g. "Career Studio". */
  title: string;
  /** One sentence describing what this used to do. */
  description: string;
}

/**
 * Placeholder rendered in place of features that wrote data, spent AI
 * credits, or took payments. The route stays addressable so the archived
 * site keeps its full sitemap, but the tool itself is switched off.
 */
export const ArchivedFeaturePage = ({ title, description }: ArchivedFeaturePageProps) => {
  useSeoMetadata({
    title: `${title} — archived | Rack & Role`,
    description: `${title} is no longer available. Rack & Role was archived on ${ARCHIVE_DATE} and is preserved as a read-only snapshot.`,
    robots: "noindex, follow",
  });

  return (
    <main className="container mx-auto flex max-w-2xl flex-col items-center px-4 py-24 text-center">
      <span className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-full border border-border bg-muted/40">
        <Archive className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
      </span>
      <h1 className="text-3xl font-bold tracking-tight">{title} is archived</h1>
      <p className="mt-4 text-muted-foreground">{description}</p>
      <p className="mt-3 text-sm text-muted-foreground">
        Rack & Role was frozen on {ARCHIVE_DATE}. Everything that created, changed, or charged for
        data is switched off — the rest of the platform stays browsable exactly as it was.
      </p>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <Link to="/archive">
          <Button variant="premium" className="gap-2">
            Read the archive notice
          </Button>
        </Link>
        <Link to="/jobs">
          <Button variant="outline" className="gap-2">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Browse the job snapshot
          </Button>
        </Link>
      </div>
    </main>
  );
};

export default ArchivedFeaturePage;
